import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { api } from "../../lib/api";
import { formatARS } from "../../lib/site";

const WEEK = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];
const monthOf = (iso) => iso.slice(0, 7);
const addMonth = (m, n) => { const [y, mo] = m.split("-").map(Number); const x = new Date(y, mo - 1 + n, 1); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`; };
const title = (m) => { const s = new Date(`${m}-01T12:00`).toLocaleDateString("es-AR", { month: "long", year: "numeric" }); return s[0].toUpperCase() + s.slice(1); };
const short = (p) => (p >= 1000 ? `$${Math.round(p / 1000)} mil` : formatARS(p));
const thisMonth = () => monthOf(new Date().toLocaleDateString("en-CA", { timeZone: "America/Argentina/Buenos_Aires" }));

const Cell = ({ x, active, cheap, onPick, testid }) => {
  const day = Number(x.date.slice(8));
  if (!x.trips) return <div data-testid={testid} className={`grid h-14 place-items-center rounded-xl text-sm sm:h-16 ${x.past ? "text-black/25" : "bg-black/[0.03] text-black/40"}`}>{day}</div>;
  return (
    <button type="button" onClick={() => onPick(x.date)} data-testid={testid} title={cheap ? `Promoción ${x.promo}% OFF · desde ${formatARS(x.price)}` : `Desde ${formatARS(x.price)}`}
      className={`relative flex h-14 flex-col items-center justify-center rounded-xl text-sm font-semibold transition-colors duration-200 sm:h-16 ${active ? "bg-[var(--forest)] text-white" : cheap ? "bg-[var(--lime)] ring-2 ring-[var(--forest)]" : "bg-white ring-1 ring-[var(--forest)]/35 hover:bg-[var(--lime)]/50"}`}>
      {cheap && <span data-testid={`${testid}-cheapest`} title="Promoción" className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[var(--ink)] px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-[var(--lime)] sm:text-[10px]">PROMO</span>}
      {day}
      <span className={`text-[11px] font-bold leading-tight sm:text-xs ${active ? "text-[var(--lime)]" : "text-[var(--forest)]"}`}>{short(x.price)}</span>
    </button>
  );
};

/** Calendario de un mes con los días que tienen salida y el precio más bajo de cada uno. */
export const MonthCalendar = ({ o, d, f, open, onClose, onPick, testid }) => {
  const [month, setMonth] = useState(monthOf(f));
  const [data, setData] = useState(null);
  useEffect(() => { if (open) setMonth(monthOf(f)); }, [open, f]);
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setData(null);
    api.get("/trips/calendar-month", { params: { origin: o, destination: d, month } })
      .then((r) => { if (alive) setData(Array.isArray(r.data?.days) ? r.data.days : []); })
      .catch(() => { if (alive) setData([]); });
    return () => { alive = false; };
  }, [open, o, d, month]);
  const lead = (new Date(`${month}-01T12:00`).getDay() + 6) % 7;
  const any = (data ?? []).some((x) => x.trips);
  const promos = (data ?? []).filter((x) => x.trips && x.promo > 0);
  const cheapest = promos.length ? Math.min(...promos.map((x) => x.price)) : null;
  const best = promos.length ? Math.max(...promos.map((x) => x.promo)) : 0;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={`${testid}-dialog`} className="max-w-lg rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Salidas de {o} a {d}</DialogTitle>
          <DialogDescription>Tocá un día con precio para ver sus horarios.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={() => setMonth(addMonth(month, -1))} disabled={month <= thisMonth()} data-testid={`${testid}-prev`} aria-label="Mes anterior"
            className="grid h-11 w-11 place-items-center rounded-full bg-[var(--forest)] text-white transition-opacity disabled:opacity-30"><ChevronLeft className="h-6 w-6" strokeWidth={2.75} /></button>
          <p data-testid={`${testid}-title`} className="font-display text-xl">{title(month)}</p>
          <button type="button" onClick={() => setMonth(addMonth(month, 1))} disabled={month >= addMonth(thisMonth(), 12)} data-testid={`${testid}-next`} aria-label="Mes siguiente"
            className="grid h-11 w-11 place-items-center rounded-full bg-[var(--forest)] text-white transition-opacity disabled:opacity-30"><ChevronRight className="h-6 w-6" strokeWidth={2.75} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center font-mono text-[11px] uppercase text-[var(--muted-ink)]">{WEEK.map((w) => <span key={w}>{w}</span>)}</div>
        {!data ? <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[var(--forest)]" /></div> : (
          <div data-testid={`${testid}-grid`} className="grid grid-cols-7 gap-1">
            {Array.from({ length: lead }, (_, i) => <span key={`e${i}`} />)}
            {data.map((x) => <Cell key={x.date} x={x} active={x.date === f} cheap={x.trips > 0 && x.promo > 0} onPick={(v) => { onPick(v); onClose(); }} testid={`${testid}-day-${x.date}`} />)}
          </div>
        )}
        {data && cheapest !== null && <p data-testid={`${testid}-cheapest-note`} className="flex items-center justify-center gap-2 text-sm"><span className="h-3 w-3 rounded bg-[var(--lime)] ring-2 ring-[var(--forest)]" />Días en promoción (hasta {best}% OFF): desde <b>{formatARS(cheapest)}</b></p>}
        {data && !any && <p data-testid={`${testid}-empty`} className="text-center text-sm text-[var(--muted-ink)]">No hay salidas este mes. Probá con el mes siguiente.</p>}
      </DialogContent>
    </Dialog>
  );
};
